import createServerInstance from "../server";
import { QueryFnParams } from "@app/utils/routesTypes";
import { SymbolInfo, TDK } from "./model";
import { FooterInfo } from "./model/footerInfo";
import {
  SpotTDKLanguageKeys,
  getFuturesLangKeys,
  getFuturesInfoLangKeys,
  getTradingBotLangKeys,
  FuturesHomeTDKLanguageKeys,
  TradingBotHomeTDKLanguageKeys,
  C2CHomeTDKLanguageKeys,
  C2CDetailTDKLanguageKeys,
  C2CHomeWithTabTDKLanguageKeys,
  C2CHomePreMarketTDKLanguageKeys,
  C2CHomePreMarketHistoryTDKLanguageKeys,
  C2CHomePreMarketDetailTDKLanguageKeys,
} from "seo/TDKLangKeys";
import { BusinessLineEnum } from "constants/base";
import { DEFAULT_LANG, LanguageTypeMap } from "../../constants/vars";

const serverInstance = createServerInstance("");

/**
 * 获取交易对信息
 */
export const getSymbolInfo = (params): Promise<SymbolInfo> => {
  return serverInstance.post("/v1/mix/public/getSymbolInfo", params);
};

/**
 * 获取服务端 tdk
 */
export const getTranslationContent = (data) => {
  return {};
  return serverInstance.post("/v1/mix/public/getTdkMltQueryList", data);
};

export const getFooterInfo = (data?): Promise<FooterInfo> => {
  return serverInstance.post("/v1/mix/index/footer", data);
};

/**
 * 拉取合约总览页头部交易数据
 */
export const fetchFutureHomeHeaderInfo = (): Promise<{
  volume24h: string;
  openInterest: string;
}> =>
  serverInstance.post("/v1/cms/seo/exchange/listing", {
    exchangeName: "cointr",
  });

/**
 * 拉取合约总览页 banner 列表
 */
export const fetchFutureHomeBanners = ({
  params,
}: QueryFnParams): Promise<
  {
    imgUrl: string;
    jumpUrl: string;
  }[]
> =>
  serverInstance.post("/v1/mix/index/locationBanner", {
    locale: params.locales,
    bannerTerminalType: 1,
    locations: [67],
  });

/**
 * 拉取合约总览页公告
 */
export const fetchFutureHomeNotices = ({
  params,
}: QueryFnParams): Promise<
  {
    contentId: string;
    showTime: string;
    title: string;
    jumpUrl: string;
  }[]
> => {
  return serverInstance.post(
    "/v1/cms/helpCenter/content/newest/announce/list",
    {
      languageId: LanguageTypeMap[params.locales || DEFAULT_LANG],
      sectionIdList: [12508313426267],
    }
  );
};

// ({ params }: QueryFnParams) =>
//   serverInstance
//       .post<{ contractNotice?: { title?: string; noticeUrl?: string }[] }>(
//           '/v1/mix/index/importance',
//           { locale: params.locales }
//       )
//       .then(d => d.contractNotice ?? [])

/**
 * 拉取合约总览页榜单
 */
export const fetchFutureHomeQuotation = (): Promise<
  Record<
    string,
    {
      symbolCodeDisplayName?: string;
      symbolDisplayName?: string;
      rose?: number;
      last?: string;
      vol?: string;
      symbolId?: string;
      symbolType?: number;
      baseSymbol?: string;
      pricedSymbol?: string;
      businessLine?: BusinessLineEnum;
      symbolCode?: string;
    }[]
  >
> => serverInstance.get("/v1/mix/market/ct/getQuotation");

export const fetchCoinList = (): Promise<
  {
    coinId: number;
    coinName: string;
    imgUrl: string;
    status: number;
  }[]
> => serverInstance.post("/v1/mix/public/getCoinConfigList");

export class TDKServices {
  /**
   * 获取交易对信息
   */
  static getSymbolInfo({ params }: QueryFnParams): Promise<SymbolInfo> {
    return serverInstance.post("/v1/mix/public/getSymbolInfo", {
      symbolId: params.symbolId,
    });
  }

  static getTranslationContent(data) {
    return {}
    // return serverInstance
    //   .post<TDK>("/v1/mix/public/getTdkMltQueryList", data)
    //   .then((res) => {
    //     return res?.reduce((acc, cur) => {
    //       acc[cur?.translationKey] = cur?.translationContent;
    //       return acc;
    //     }, {});
    //   });
  }

  /**
   * 现货TDK
   */
  static getSpotTranslationContent({ params }: QueryFnParams) {
    const { titleKey, descKey } = SpotTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }

  /**
   * 合约TDK
   */
  static getFuturesTranslationContent({ params }: QueryFnParams) {
    const { businessLine, symbolId, locales } = params;
    const { titleKey, descKey } = getFuturesLangKeys(businessLine, symbolId);
    return TDKServices.getTranslationContent({
      locale: locales,
      translationKeys: [titleKey, descKey],
    });
  }

  /**
   * 合约信息TDK
   */
  static getFuturesInfoTranslationContent({ params, pathname }: QueryFnParams) {
    const { locales } = params;
    const { titleKey, descKey } = getFuturesInfoLangKeys(
      locales ? pathname.replace(`/${locales}`, "") : pathname
    );
    return TDKServices.getTranslationContent({
      locale: locales,
      translationKeys: [titleKey, descKey],
    });
  }

  /**
   * 策略TDK
   */
  static getTradingBotTranslationContent({
    params,
    search,
    pathname,
  }: QueryFnParams) {
    const { locales } = params;
    const { titleKey, descKey } = getTradingBotLangKeys(pathname, search);
    return TDKServices.getTranslationContent({
      locale: locales,
      translationKeys: [titleKey, descKey],
    });
  }
  /**
   * 合约首页TDK
   */
  static getFuturesHomeTranslationContent({ params }: QueryFnParams) {
    const { titleKey, descKey } = FuturesHomeTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }
  /**
   * 策略首页TDK
   */
  static getTradingBotHomeTranslationContent({ params }: QueryFnParams) {
    const { titleKey, descKey } = TradingBotHomeTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }
  /**
   * C2C首页TDK
   */
  static getC2CHomeTranslationContent({ params }: QueryFnParams) {
    const { titleKey, descKey } = params.quotation
      ? C2CHomeWithTabTDKLanguageKeys
      : C2CHomeTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }
  /**
   * C2C详情页TDK
   */
  static getC2CDetailTranslationContent({ params, pathname }: QueryFnParams) {
    const { titleKey, descKey } = pathname.includes("pre-market")
      ? C2CHomePreMarketDetailTDKLanguageKeys
      : C2CDetailTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }
  /**
   * C2C首页TDK
   */
  static getC2CHomePreMarketTranslationContent({
    params,
    pathname,
  }: QueryFnParams) {
    const { titleKey, descKey } =
      pathname?.indexOf("/pre-market/history") > -1
        ? C2CHomePreMarketHistoryTDKLanguageKeys
        : C2CHomePreMarketTDKLanguageKeys;
    return TDKServices.getTranslationContent({
      locale: params.locales,
      translationKeys: [titleKey, descKey],
    });
  }
}

/**
 * 拉取合约总览页榜单
 */
export const fetchFutureHoListQuotation = (): Promise<
  Record<
    string,
    {
      symbolDisplayName?: string;
      rose?: number;
      last?: string;
      vol?: string;
      symbolId?: string;
      symbolType?: number;
      baseSymbol?: string;
      pricedSymbol?: string;
      businessLine?: BusinessLineEnum;
      symbolCode?: string;
    }[]
  >
> => serverInstance.get("/v1/mix/market/ct/getQuotation");

export interface ICoinFutures {
  businessLine: number;
  contractType: number;
  kline: Array<number>;
  labelId: number;
  margin: string | number;
  onLineTime: string | number;
  openTime: string | number;
  reverse: boolean;
  symbol: string;
  symbolCode: string;
  symbolId: string;
  before24Open: string;
  last: string;
  symbolCodeDisplayName: string;
}
interface HotCoinsProps {
  coinFutures: Array<ICoinFutures>;
}
// 热门币对
export function getHotCoinList(): Promise<HotCoinsProps> {
  return serverInstance.post("v1/mix/market/hotCoinSearchList", { type: 1 });
}
